import { DOCUMENT } from "@angular/common";
import { Component, Inject, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from "@angular/core";
import { MediaObserver } from "@angular/flex-layout";
import { MatSidenav } from '@angular/material/sidenav';
import { Router } from '@angular/router';
import { StorageService } from "@shared";
import { Subscription } from "rxjs";
import { AuthContextModel } from './Auth/AuthContextModel';
import { AuthService } from "./Auth/AuthService";

@Component({
	selector: "app",
	templateUrl: "./AppComponent.html",
	styleUrls: ["./AppComponent.scss"],
	encapsulation: ViewEncapsulation.None,
})
export class AppComponent implements OnInit, OnDestroy {
	private readonly sidenavOpenedStorageKey = "app.sidenavOpened";
	private readonly useDarkThemeStorageKey = "app.useDarkTheme";

	public sidenavMode: "over" | "side";
	public sidenavOpened: boolean;
	@ViewChild("sidenav") public sidenav: MatSidenav;
	public useDarkTheme: boolean = false;
	public authContext: AuthContextModel;


	private authContextSubscription: Subscription;
	private mediaSubscription: Subscription;

	constructor(
		@Inject(DOCUMENT) private document: Document,
		private authService: AuthService,
		private media: MediaObserver,
		private router: Router,
		private storageService: StorageService) {
		this.setSidenavMode();
		this.setSidenavOpened();
		this.setActiveTheme();
	}

	public ngOnInit(): void {
		this.mediaSubscription = this.media.media$
			.subscribe(() => {
				this.setSidenavMode();
			});

		this.authContextSubscription = this.authService
			.authContext
			.subscribe(authContext => {
				this.authContext = authContext;
			});

		this.authService.setAuthContext();
	}

	public ngOnDestroy(): void {
		this.authContextSubscription.unsubscribe();
		this.mediaSubscription.unsubscribe();
	}

	public logoutClicked(): void {
		this.router.navigate(["/logout"]);
	}

	public sidenavOpenChanged(opened: boolean): void {
		this.sidenavOpened = opened;
		this.storageService.setLocal(this.sidenavOpenedStorageKey, this.sidenavOpened);
	}

	public sidenavItemClicked(): void {
		if (this.sidenavMode == "over") {
			this.sidenavOpened = false;
		}
	}

	public darkModeToggled(): void {
		this.useDarkTheme = !this.useDarkTheme;

		this.storageService.setLocal(this.useDarkThemeStorageKey, this.useDarkTheme);

		this.setThemeClass();
	}

	public toggleSidenavClicked(): void {
		this.sidenav.toggle();
	}

	private setSidenavMode(): void {
		this.sidenavMode = this.media.isActive("xs") ? "over" : "side";
	}

	private setSidenavOpened(): void {
		let opened = this.storageService.getLocal<boolean>(this.sidenavOpenedStorageKey);

		if (opened === null) {
			opened = true; // default to open
		}

		if (opened) {
			this.sidenavOpened = this.sidenavMode != "over"; // always collapsed on mobile by default
		}
		else {
			this.sidenavOpened = false;
		}
	}

	private setActiveTheme(): void {
		this.useDarkTheme = this.storageService.getLocal(this.useDarkThemeStorageKey);

		if (this.useDarkTheme === null) {
			this.useDarkTheme = false; // default
		}

		this.setThemeClass();
	}

	private setThemeClass(): void {
		let bodyClasses = this.document.body.classList;

		if (this.useDarkTheme) {
			bodyClasses.remove("light-theme");
			bodyClasses.add("dark-theme");
		}
		else {
			bodyClasses.remove("dark-theme");
			bodyClasses.add("light-theme");
		}
	}
}
